import { useEffect, useState } from "react";
import dayjs from "dayjs";

import {
  Box,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconCheck, IconEdit, IconStar, IconTrash } from "@tabler/icons-react";
import { blueGrey, green, red } from "@mui/material/colors";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import useCustomSnackbar from "hooks/customSnackbar";
import CurrentSession from "data/userSession";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ImageType = any;

// Metadata panel for a photo (date, album, caption + star). Shown inside the
// PhotoGrid accordion. The image itself lives in the thumbnail above, so it's
// intentionally not repeated here.
const PhotoDetails = ({
  image,
  editable = false,
  onChange = () => {},
}: {
  image: ImageType;
  editable?: boolean;
  onChange?: () => void;
}) => {
  const { enqueueSnackbar } = useCustomSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [album, setAlbum] = useState(image.album);
  const [caption, setCaption] = useState(image.caption || "");
  const [date, setDate] = useState(dayjs(image.date));
  const [favorited, setFavorited] = useState(image.favorited);
  const [authMatches, setAuthMatches] = useState(false);

  useEffect(() => {
    CurrentSession.load().then(() => {
      setAuthMatches(CurrentSession.authenticationStrictlyMatches(image.uid));
    });
  }, [image.uid]);

  // Re-sync when a different photo is selected (same instance is reused).
  useEffect(() => {
    setIsEditing(false);
    setAlbum(image.album);
    setCaption(image.caption || "");
    setDate(dayjs(image.date));
    setFavorited(image.favorited);
  }, [image]);

  const canEdit = editable && authMatches;

  const handleSave = async () => {
    setIsEditing(false);
    try {
      // Await the write: save() only invalidates the query cache once the row
      // commits, so refetching before it resolves reads stale data and reverts
      // the edit.
      await image.save();
    } catch (e) {
      enqueueSnackbar((e as Error).message, { variant: "error" });
    }
    onChange();
  };

  const onDelete = () => {
    if (!window.confirm("Delete this photo? This can't be undone.")) return;
    image
      .delete()
      .then(() => {
        enqueueSnackbar("Image deleted", { variant: "info" });
        onChange();
      })
      .catch((e: Error) => {
        enqueueSnackbar(e.message, { variant: "error" });
      });
  };

  return (
    <Box sx={{ width: "100%", px: 2 }}>
      {isEditing ? (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={date}
              onChange={(newDate) => {
                if (!newDate) return;
                image.date = newDate.toDate();
                setDate(newDate);
              }}
              disableFuture
              slotProps={{
                textField: { variant: "standard", fullWidth: true },
              }}
            />
          </LocalizationProvider>
          <TextField
            label="Album Name"
            value={album}
            onChange={(e) => {
              image.album = e.target.value;
              setAlbum(image.album);
            }}
            variant="standard"
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            label="Caption"
            value={caption}
            onChange={(e) => {
              image.caption = e.target.value;
              setCaption(image.caption);
            }}
            variant="standard"
            multiline
            fullWidth
            sx={{ mt: 1 }}
          />
          <Grid container justifyContent="space-between" sx={{ mt: 1 }}>
            <IconButton onClick={onDelete} sx={{ color: red[400] }}>
              <IconTrash />
            </IconButton>
            <IconButton onClick={handleSave} sx={{ color: green[500] }}>
              <IconCheck />
            </IconButton>
          </Grid>
        </>
      ) : (
        <>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            wrap="nowrap"
          >
            <Typography
              variant="body2"
              sx={{
                mr: 1,
                color: caption ? blueGrey[700] : blueGrey[400],
                fontStyle: caption ? "normal" : "italic",
              }}
            >
              {caption || "No caption"}
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              {canEdit && (
                <IconButton onClick={() => setIsEditing(true)}>
                  <IconEdit />
                </IconButton>
              )}
              <Tooltip title="Show on Progress Page" arrow>
                <span>
                  <IconButton
                    disabled={!canEdit}
                    onClick={
                      canEdit
                        ? () => {
                            image.toggleFavorited();
                            setFavorited(image.favorited);
                            handleSave();
                          }
                        : undefined
                    }
                  >
                    {favorited ? (
                      <IconStar color="#ffc107" fill="#ffc107" />
                    ) : (
                      <IconStar color={blueGrey[100]} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default PhotoDetails;
